/**
 * Font loading composable
 * Handles loading custom fonts from the public folder and combining with Google Fonts.
 */

import { FONT_FAMILIES as GOOGLE_FONT_FAMILIES } from '~/utils/designConstants'

export type FontVariant = {
  weight?: number
  style: 'normal' | 'italic'
  src: string
  format?: string
}

export type CustomFont = {
  family: string
  variants: FontVariant[]
  fallback: string
}

export type FontConfig = {
  customFonts: CustomFont[]
}

// Import all font configs using Vite's glob import
const fontConfigs = import.meta.glob<FontConfig>('../data/fonts/*.json', {
  eager: true,
})

// Track which fonts have been loaded to avoid duplicate @font-face rules
const loadedFonts = new Set<string>()

/**
 * Inject @font-face rules into the document for a custom font
 */
function injectFontFace(font: CustomFont): void {
  if (loadedFonts.has(font.family)) return

  const styleEl = document.createElement('style')
  styleEl.id = `custom-font-${font.family.replace(/\s+/g, '-')}`

  const fontFaceRules = font.variants
    .map((variant) => {
      // Determine format based on file extension
      const ext = variant.src.split('?')[0]?.split('.').pop()?.toLowerCase()
      let format = variant.format || 'opentype'
      switch (ext) {
        case 'ttf': {
          format = 'truetype'
          break
        }
        case 'woff': {
          format = 'woff'
          break
        }
        case 'woff2': {
          format = 'woff2'
          break
        }
      }

      return `
@font-face {
  font-family: '${font.family}';
  src: url('${variant.src}') format('${format}');
  ${variant.weight === undefined ? '' : `font-weight: ${variant.weight};`}
  font-style: ${variant.style};
  font-display: swap;
}`
    })
    .join('\n')

  styleEl.textContent = fontFaceRules
  document.head.append(styleEl)
  loadedFonts.add(font.family)
}

/**
 * Preload font files to avoid FOUT
 */
function preloadFont(font: CustomFont): void {
  font.variants.forEach((variant) => {
    // Check if already preloaded
    const existing = document.querySelector(`link[href="${variant.src}"]`)
    if (existing) return

    const link = document.createElement('link')
    link.rel = 'preload'
    link.as = 'font'
    link.href = variant.src
    link.crossOrigin = 'anonymous'
    document.head.append(link)
  })
}

export function useFonts() {
  const { fontsFile } = useStoreConfig()
  const theme = useTheme()

  // Load font config for the current store
  const fontConfig = computed<FontConfig | null>(() => {
    const file = fontsFile.value
    if (!file) return null

    // Try to find the config file
    const key = `../data/fonts/${file}.json`
    const config = fontConfigs[key]
    return config || null
  })

  const allConfigFonts = computed<CustomFont[]>(() => {
    const fonts: CustomFont[] = []
    for (const config of Object.values(fontConfigs)) {
      if (config?.customFonts?.length) fonts.push(...config.customFonts)
    }
    return fonts
  })

  const dynamicContentFont = computed<CustomFont | null>(() => {
    const family = theme.contentFontFamily.value
    const src = theme.contentFontSrc.value
    if (!family || !src) return null

    return {
      family,
      variants: [
        {
          style: 'normal' as const,
          src,
          format: theme.contentFontFormat.value,
        },
      ],
      fallback: 'sans-serif',
    }
  })

  const dynamicBuilderUiFont = computed<CustomFont | null>(() => {
    const family = theme.builderUiFontFamily.value
    const src = theme.builderUiFontSrc.value
    if (!family || !src) return null

    const weight = parseInt(theme.builderUiFontWeight.value) || 400
    return {
      family,
      variants: [
        {
          weight,
          style: 'normal' as const,
          src,
          format: theme.builderUiFontFormat.value,
        },
      ],
      fallback: 'sans-serif',
    }
  })

  // Custom fonts from config (store first, then any others)
  const customFonts = computed<CustomFont[]>(() => {
    const storeFonts = fontConfig.value?.customFonts || []
    const merged = new Map<string, CustomFont>()

    if (dynamicContentFont.value) {
      merged.set(dynamicContentFont.value.family, dynamicContentFont.value)
    }
    if (
      dynamicBuilderUiFont.value &&
      !merged.has(dynamicBuilderUiFont.value.family)
    ) {
      merged.set(dynamicBuilderUiFont.value.family, dynamicBuilderUiFont.value)
    }

    for (const font of storeFonts) {
      if (!merged.has(font.family)) merged.set(font.family, font)
    }
    for (const font of allConfigFonts.value) {
      if (!merged.has(font.family)) merged.set(font.family, font)
    }
    return [...merged.values()]
  })

  // Combined font families for dropdowns (custom + Google)
  const allFontFamilies = computed<string[]>(() => {
    const custom = customFonts.value.map((f) => `${f.family}, ${f.fallback}`)
    // Put custom fonts first, then Google fonts
    return [...custom, ...GOOGLE_FONT_FAMILIES]
  })

  // Font menu items for select dropdowns
  const fontMenuItems = computed(() => {
    return allFontFamilies.value.map((f) => ({
      label: f.split(',')[0]!.trim(),
      value: f,
      style: { fontFamily: f },
    }))
  })

  // Loading state
  const fontsLoaded = ref(false)

  // Load and inject custom fonts
  async function loadCustomFonts(): Promise<void> {
    const fonts = customFonts.value
    if (fonts.length === 0) {
      fontsLoaded.value = true
      return
    }

    // Preload and inject all fonts
    fonts.forEach((font) => {
      preloadFont(font)
      injectFontFace(font)
    })

    // Wait for fonts to actually load using FontFace API
    try {
      await Promise.all(
        fonts.map(async (font) => {
          // Load each variant
          await Promise.all(
            font.variants.map(async (variant) => {
              if (variant.weight === undefined) {
                await document.fonts.load(`1em "${font.family}"`)
                return
              }
              const fontFace = new FontFace(
                font.family,
                `url(${variant.src})`,
                { weight: String(variant.weight), style: variant.style },
              )
              await fontFace.load()
              document.fonts.add(fontFace)
            }),
          )
        }),
      )
    } catch (error_) {
      console.warn('Some fonts failed to load:', error_)
    }

    fontsLoaded.value = true
  }

  // Auto-load fonts on mount when in client
  if (import.meta.client) {
    onMounted(() => {
      loadCustomFonts()
    })
  }

  return {
    fontConfig,
    customFonts,
    allFontFamilies,
    fontMenuItems,
    fontsLoaded,
    loadCustomFonts,
  }
}
